import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Inject, OnDestroy, OnInit, Renderer2, TemplateRef, ViewChild, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { debounceTime, map, startWith, takeUntil, tap } from 'rxjs/operators';
import { FuseConfirmationService } from '@fuse/services/confirmation';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { assign } from 'lodash';
import { AlertMessageService } from 'app/core/common/alert.message.service';
import { ReportsService } from 'app/modules/admin/transactions/inwards/reports.service';
import { ApprovedMeasurementsService } from '../approved-measurement.service';
import { ApprovedMeasurement } from '../approved-measurement.types';
import { CommonDropDown, SingleCommonDropDown } from 'app/core/common/types/dropdown.types';
import { LookupService } from 'app/core/common/services/lookup.service';

@Component({
    selector: 'approved-measurement-details',
    templateUrl: './approved-measurement-details.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ApprovedMeasurementDetailsComponent implements OnInit, OnDestroy {

    approvedMeasurement: ApprovedMeasurement;
    approvedMeasurementForm: FormGroup;
    dialogTitle = `New Approved Measurement`
    alert: any;
    reportType: string;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    total: number;
    options: CommonDropDown[];
    filteredOptions: Observable<CommonDropDown[]>;
    selectedType: string;
    approvedMeasurementFilterForm: FormGroup;
    @ViewChild('particularInput', { static: false }) particularInput: ElementRef;
    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        @Inject(MAT_DIALOG_DATA) private _data: {
            approvedMeasurement: ApprovedMeasurement,
            reportType: string,
            selectedType: string
        },
        private approvedMeasurementService: ApprovedMeasurementsService,
        private _matDialogRef: MatDialogRef<ApprovedMeasurementDetailsComponent>,
        private _formBuilder: FormBuilder,
        private _fuseConfirmationService: FuseConfirmationService,
        private _alertMessage: AlertMessageService,
        private _lookupService: LookupService
    ) {
        this.reportType = this._data.reportType;
        this.selectedType = this._data.selectedType;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {

        // Open the drawer
        this.approvedMeasurementForm = this._formBuilder.group({
            id: [''],
            reportId: [this._data.approvedMeasurement.reportId],
            type: ['', [Validators.required]],
            particular: [''],
            width: [this._data.approvedMeasurement.width],
            depth: [this._data.approvedMeasurement.depth],
            total: [this._data.approvedMeasurement.total],
            orderIndex: [this._data.approvedMeasurement.orderIndex],
        });

        this._lookupService.getOptionByTypeId(92, this.reportType).subscribe((res) => {
            this.options = res;
            this.filteredOptions = this.approvedMeasurementForm.get("particular")
                .valueChanges.pipe(
                    startWith(''),
                    map(value => this._optionsfilter(value || '')),
                );
        })

        if (this._data.approvedMeasurement.id) {
            // Request the data from the server
            this.approvedMeasurementService.getApprovedMeasurement(this._data.approvedMeasurement.id)
                .subscribe((res) => {
                    if (res.isSuccess) {
                        this.approvedMeasurement = res.result;
                        this.dialogTitle = `Edit Approved Measurement`;
                        this.approvedMeasurementForm.patchValue(this.approvedMeasurement);
                    }
                    else {
                        this._alertMessage.error(res.errors.join('\n'));
                        this.onClose();
                    }
                });
        }

        this.approvedMeasurementForm.valueChanges
            .pipe(
                debounceTime(500),
                takeUntil(this._unsubscribeAll)
            )
            .subscribe((value) => {
                if (value.width && value.depth) {
                    const totalValue = value.width * value.depth;
                    this.approvedMeasurementForm.get('total').patchValue(Number(totalValue.toFixed(2)));
                }
            });

        this.defaultRadioValue();
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Close the drawer
     */
    // closeDrawer(): Promise<MatDrawerToggleResult> {
    //     return this._optionTypesListComponent.matDrawer.close();
    // }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }
    setResetForm(oldData: ApprovedMeasurement) {

        this._data.approvedMeasurement as ApprovedMeasurement;
        this._data.approvedMeasurement.reportId = oldData.reportId;
        this._data.approvedMeasurement.orderIndex = oldData.orderIndex + 1;
        this._data.approvedMeasurement.type = oldData.type;
        this.approvedMeasurementForm.reset();
        this.approvedMeasurementForm.patchValue(this._data.approvedMeasurement);
    }
    saveAndNew() {
        const approvedMeasurement = this.approvedMeasurementForm.getRawValue();
        this.save(approvedMeasurement, true)
        setTimeout(() => this.particularInput.nativeElement.focus(), 0);
    }
    saveAndClose() {
        const approvedMeasurement = this.approvedMeasurementForm.getRawValue();
        this.save(approvedMeasurement);


    }
    save(approvedMeasurement, isOpen = false) {
        this.approvedMeasurementService.createApprovedMeasurement(approvedMeasurement).subscribe((response) => {
            if (response.isSuccess) {
                // Show a success message
                this._alertMessage.success("Approved measurement added successfully");
                // Close the dialog
                if (!isOpen) {
                    this._matDialogRef.close(response);
                }
                else {
                    this.setResetForm(approvedMeasurement);
                }
            }
            else {
                this._alertMessage.error(response.errors.join("\n"));
            }
        });
    }
    updateAndClose() {

        const approvedMeasurement = this.approvedMeasurementForm.getRawValue();

        this.approvedMeasurementService.updateApprovedMeasurement(approvedMeasurement).subscribe((response) => {
            if (response.isSuccess) {
                // Show a success message
                this._alertMessage.success("Approved Measurement updated successfully");
                // Close the dialog
                this._matDialogRef.close(response);
            }
            else {
                this._alertMessage.error(response.errors.join("\n"));
            }
        });

    }

    onClose(): void {
        this._matDialogRef.close();
    }

    displayOptionsFn(options: CommonDropDown[]): (name: string) => string {
        return (name: string) => {
            const correspondingOption = Array.isArray(options) ? options.find(option => option.name === name) : null;
            return correspondingOption ? correspondingOption.name : '';
        }
    }

    private _optionsfilter(name: string): CommonDropDown[] {
        const filterValue = name.toString().toLowerCase();

        return this.options.filter(option => option.name.toLowerCase().includes(filterValue));
    }

    defaultRadioValue() {

        if (this.selectedType == 'Land') {
            this.approvedMeasurementForm.controls['type'].setValue('Land Area');
        }
        else {

            this.approvedMeasurementForm.controls['type'].setValue('Carpet Area');

        }
    }
}
